import axios from "axios";
import config from "../configs/Configs.json";

const { API__SERVER } = config;

// Tạo axios instance với base URL từ Configs.json
const apiClient = axios.create({
  baseURL: API__SERVER,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor để tự động thêm token vào headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("IuDiToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============ AUTH APIs ============

/**
 * Đăng nhập
 * @param {Object} credentials - {Username, Password, Latitude, Longitude, LastLoginIP}
 * @returns {Promise}
 */
export const loginUser = async (credentials) => {
  try {
    const response = await apiClient.post("/login", credentials);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Đăng ký
 * @param {Object} userData - {Username, FullName, Email, Gender, Password, Cf_Password, Latitude, Longitude, LastLoginIP, avatarLink}
 * @returns {Promise}
 */
export const registerUser = async (userData) => {
  try {
    const response = await apiClient.post("/register", userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Đăng xuất
 * @param {string} userId
 * @returns {Promise}
 */
export const logoutUser = async (userId) => {
  try {
    const response = await apiClient.post(`/logout/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Quên mật khẩu
 * @param {string} email
 * @returns {Promise}
 */
export const forgotPassword = async (email) => {
  try {
    const response = await axios.post(
      "https://iudi-api.thinkdiff.us/api/forgotPassword",
      { Email: email }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Đổi mật khẩu
 * @param {string} userId
 * @param {Object} passwordData - {Password, NewPassword, CfNewPassword}
 * @returns {Promise}
 */
export const changePassword = async (userId, passwordData) => {
  try {
    const response = await apiClient.patch(
      `/profile/change_password/${userId}`,
      passwordData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Đổi số điện thoại
 * @param {string} userId
 * @param {Object} phoneData - {Phone}
 * @returns {Promise}
 */
export const changePhoneNumber = async (userId, phoneData) => {
  try {
    const response = await apiClient.patch(
      `/profile/phone/${userId}`,
      phoneData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cập nhật toàn bộ profile
 * @param {string} userId
 * @param {Object} profileData
 * @returns {Promise}
 */
export const updateProfile = async (userId, profileData) => {
  try {
    const response = await apiClient.put(
      `/profile/change_profile/${userId}`,
      profileData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Lấy thông tin profile user khác
 * @param {string} userId
 * @returns {Promise}
 */
export const getUserProfile = async (userId) => {
  try {
    const response = await apiClient.get(`/profile/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============ LOCATION APIs ============

/**
 * Lấy vị trí từ IP
 * @returns {Promise}
 */
export const getLocationFromIP = async () => {
  try {
    // Thử API đầu tiên: ipapi.co (miễn phí, không cần key, hỗ trợ CORS)
    const response = await axios.get("https://ipapi.co/json/");
    return {
      latitude: response.data.latitude,
      longitude: response.data.longitude,
      ip: response.data.ip,
      city: response.data.city,
      region: response.data.region,
      country: response.data.country_name,
    };
  } catch (error) {
    console.error(
      "Error fetching location from ipapi.co, trying backup...",
      error
    );

    // Fallback: Thử API thứ 2
    try {
      const backupResponse = await axios.get(
        "https://ipinfo.io/json?token=free"
      );
      const [lat, lon] = backupResponse.data.loc?.split(",") || ["0", "0"];
      return {
        latitude: parseFloat(lat),
        longitude: parseFloat(lon),
        ip: backupResponse.data.ip,
        city: backupResponse.data.city,
        region: backupResponse.data.region,
        country: backupResponse.data.country,
      };
    } catch (backupError) {
      console.error("Error fetching location from backup API:", backupError);
      // Trả về giá trị mặc định thay vì throw error
      return {
        latitude: 21.0285,
        longitude: 105.8542,
        ip: "Unknown",
        city: "Hà Nội",
        region: "Hà Nội",
        country: "Việt Nam",
      };
    }
  }
};

/**
 * Lấy địa chỉ chi tiết từ tọa độ GPS (Reverse Geocoding)
 * @param {number} latitude - Vĩ độ
 * @param {number} longitude - Kinh độ
 * @returns {Promise<Object>} - {ward, district, city, region, country, fullAddress}
 */
export const getDetailedAddress = async (latitude, longitude) => {
  try {
    // Dùng Nominatim (OpenStreetMap) - miễn phí, hỗ trợ địa chỉ Việt Nam chi tiết
    const response = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          "User-Agent": "timquanhday-app/1.0",
        },
      }
    );

    const address = response.data.address || {};

    return {
      ward: address.suburb || address.neighbourhood || address.quarter || "", // Phường
      district: address.city_district || address.county || address.town || "", // Quận
      city: address.city || address.state || "", // Thành phố
      region: address.state || address.province || "", // Tỉnh
      country: address.country || "Việt Nam",
      fullAddress: response.data.display_name || "",
      // Thông tin bổ sung
      road: address.road || "", // Tên đường
      postcode: address.postcode || "",
    };
  } catch (error) {
    console.error("Error fetching detailed address:", error);

    // Fallback: Thử API khác (LocationIQ - cũng dùng OSM data)
    try {
      const fallbackResponse = await axios.get(
        `https://us1.locationiq.com/v1/reverse.php?key=pk.0f147952a41c555c5b70614039fd148b&lat=${latitude}&lon=${longitude}&format=json`
      );

      const addr = fallbackResponse.data.address || {};
      return {
        ward: addr.suburb || addr.neighbourhood || "",
        district: addr.city_district || addr.county || "",
        city: addr.city || addr.town || "",
        region: addr.state || "",
        country: addr.country || "Việt Nam",
        fullAddress: fallbackResponse.data.display_name || "",
        road: addr.road || "",
        postcode: addr.postcode || "",
      };
    } catch (fallbackError) {
      console.error("Error from fallback geocoding API:", fallbackError);
      return null;
    }
  }
};

/**
 * Lấy vị trí chi tiết từ Browser Geolocation (chính xác hơn IP)
 * @returns {Promise<Object>}
 */
export const getLocationFromBrowser = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Lấy địa chỉ chi tiết từ tọa độ
        const detailedAddress = await getDetailedAddress(latitude, longitude);

        resolve({
          latitude,
          longitude,
          accuracy: position.coords.accuracy,
          ...detailedAddress,
        });
      },
      (error) => {
        console.error("Geolocation error:", error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Lấy Last Login IP
 * @returns {Promise}
 */
export const getLastLoginIP = async () => {
  try {
    const response = await axios.get("http://ip-api.com/json/");
    return response.data;
  } catch (error) {
    console.error("Error fetching IP:", error);
    throw error;
  }
};

export default apiClient;
